import React, { useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { RootState } from '../../../store'
import useBotAnswerEnded from '../../../hooks/useBotAnswerEnded'
import { CurrentSubView, ListStatus, RecognitionState, RetorikEvent } from '../../../models/enums'

interface MicrophoneTextBubbleProps {
  showLoader: boolean
}

const MicrophoneTextBubble = ({ showLoader }: MicrophoneTextBubbleProps): JSX.Element => {
  const botAnswerEnded = useBotAnswerEnded()
  const translation = useSelector((state: RootState) => state.localeReducer.currentTranslations)
  const isRTL = useSelector((state: RootState) => state.localeReducer.isRTL)
  const isMobile = useSelector((state: RootState) => state.viewReducer.isMobile)
  const isLandscape = useSelector((state: RootState) => state.viewReducer.isLandscape)
  const activeRecognitionState = useSelector((state: RootState) => state.speechReducer.activeRecognitionState)
  const lastRecognitionInterim = useSelector((state: RootState) => state.speechReducer.lastRecognitionInterim)
  const speaking = useSelector((state: RootState) => state.speechReducer.speaking)
  const useContinuousRecognition = useSelector((state: RootState) => state.speechReducer.useContinuousRecognition)
  const agentData = useSelector((state: RootState) => state.retorikReducer.agentData)
  const currentSubView = useSelector((state: RootState) => state.utilsReducer.currentSubView)
  const dashboardOpened = useSelector((state: RootState) => state.utilsReducer.dashboardOpened)
  const retorikEvent = useSelector((state: RootState) => state.utilsReducer.retorikEvent)
  const listStatus = useSelector((state: RootState) => state.utilsReducer.listStatus)
  const [className, setClassName] = useState<string>()
  const [text, setText] = useState<string | undefined>()
  const [textUndefined, setTextUndefined] = useState<boolean>(false)
  const timerRef = useRef<NodeJS.Timeout | null>(null)

  useEffect(() => {
    return () => {
      timerRef.current && clearTimeout(timerRef.current)
    }
  }, [])

  useEffect(() => {
    setTextUndefined(
      speaking || showLoader || !botAnswerEnded || currentSubView !== CurrentSubView.none || dashboardOpened || retorikEvent === RetorikEvent.DetailViewOpen || listStatus === ListStatus.opened
    )
  }, [speaking, showLoader, botAnswerEnded, currentSubView, dashboardOpened, retorikEvent, listStatus])

  useEffect(() => {
    let tempClassName =
      'rf-absolute rf-w-max rf-py-2 rf-px-4 rf-flex rf-flex-col rf-justify-center rf-items-center rf-bg-[#000000B3] rf-border rf-rounded-lg rf-text-truewhite rf-text-center rf-border-[#8B8888] rf-shadow-[inset_0_0_6px_#0000003B] '
    if (isMobile) {
      tempClassName += 'rf-bottom-[calc(100%+2rem)]'
    } else if (isLandscape) {
      tempClassName += 'rf-top-[calc(100%+1rem)]'
    } else {
      tempClassName += isRTL ? 'rf-left-[calc(100%+1rem)]' : 'rf-right-[calc(100%+1rem)]'
    }

    setClassName(tempClassName)
  }, [isMobile, isLandscape, isRTL])

  useEffect(() => {
    timerRef.current && clearTimeout(timerRef.current)
    switch (activeRecognitionState) {
      case RecognitionState.Listening:
        setText(isMobile || (isLandscape && lastRecognitionInterim) ? undefined : translation.microphone.listening)
        break
      case RecognitionState.Closed:
        let text: string | undefined = undefined
        if (!textUndefined) {
          text = useContinuousRecognition
            ? `<p>${translation.microphone.trigger}${isMobile ? ' ' : '<br />'}<strong>« ${agentData?.name} »</strong></p>`
            : `<p>${translation.microphone.triggerwithoutname}</p>`
        }

        // Show the button only 1.5s after, to prevents it from appearing à fraction of second between several texts
        timerRef.current = setTimeout(() => {
          setText(text)
        }, 1500)
        break
      case RecognitionState.Initializing:
      case RecognitionState.Closing:
      case RecognitionState.ForceClosing:
      default:
        setText(undefined)
        break
    }
  }, [isMobile, activeRecognitionState, agentData, lastRecognitionInterim, textUndefined, useContinuousRecognition])

  return text ? (
    <React.Fragment>
      <div
        className={className}
        style={{
          clipPath: isMobile
            ? 'polygon(0% 0%, 0% 100%, calc(50% - 0.4rem) 100%, calc(50% - 0.4rem) calc(100% - 1px), calc(50% + 0.4rem) calc(100% - 1px), calc(50% + 0.4rem) 100%, 100% 100%, 100% 0%)'
            : isLandscape
            ? 'polygon(0% 0%, 0% 100%, 100% 100%, 100% 0%, calc(50% + 0.4rem) 0%, calc(50% + 0.4rem) calc(0% + 1px), calc(50% - 0.4rem) calc(0% + 1px), calc(50% - 0.4rem) 0%)'
            : 'polygon(0% 0%, 0% 100%, 100% 100%, 100% calc(50% + 0.4rem), calc(100% - 1px) calc(50% + 0.4rem), calc(100% - 1px) calc(50% - 0.4rem), 100% calc(50% - 0.4rem), 100% 0%)'
        }}
        dangerouslySetInnerHTML={{ __html: text }}
      />

      <div
        className='rf-absolute rf-w-3 rf-h-3 rf-bg-[#000000B3] rf-border-t rf-border-r rf-border-t-[#8B8888] rf-border-r-[#8B8888]'
        style={{
          rotate: isMobile ? '135deg' : isLandscape ? '-45deg' : isRTL ? '-135deg' : '45deg',
          clipPath: 'polygon(0% 0%, 100% 0%, 100% 100%)',
          bottom: isMobile ? 'calc(100% + 1.7rem)' : undefined,
          top: isLandscape ? 'calc(100% + 0.7rem)' : undefined,
          right: isMobile || isLandscape || isRTL ? undefined : 'calc(100% + 0.7rem)',
          left: isMobile || isLandscape || !isRTL ? undefined : 'calc(100% + 0.7rem)'
        }}
      />
    </React.Fragment>
  ) : (
    <React.Fragment />
  )
}

export default MicrophoneTextBubble
